import * as React from 'react';
import { IndexRedirect, IndexRoute, Redirect, Route } from 'react-router';
import type { ChangeHook } from 'react-router';

import {
  CollectionCategoryPageDynamic,
  CollectionDiscoveryContextDynamic,
  CollectionDiscoveryPageDynamic,
  CollectionLibraryPageDynamic,
  CollectionSelectionConfirmationPageDynamic,
  LearningPathLibraryBadgePageDynamic,
  LearningPathLibraryContextDynamic,
  LearningPathLibraryDynamic,
  PresetCollectionPageDynamic,
  TaggedCollectionsPageDynamic,
} from '@coursera-web/enterprise-admin-collection-discovery';
import { LearnerPageDynamic } from '@coursera-web/enterprise-admin-learner';
import { LearnersPageDynamic, ProgramLearnersPageDynamic } from '@coursera-web/enterprise-admin-learners';
import { ProgramDetailsSettingsContainerDynamic } from '@coursera-web/enterprise-admin-program-settings';
import {
  SCIMConfigPageDynamic,
  SCIMGroupMappingDynamic,
  SCIMSetupWrapperDynamic,
} from '@coursera-web/enterprise-admin-scim';
import {
  AdminDashboardSettingsDynamic,
  CustomizeOrganizationAdminSettingsDynamic,
  IntegrationSettingsWrapperDynamic,
  UnsubscribePageDynamic,
} from '@coursera-web/enterprise-admin-settings';
import {
  ConfigureXapiSettingsPageDynamic,
  EditXapiSettingsPageDynamic,
  IntegrateSettingsDynamic,
  XApiFormsWrapper,
  XapiContextProviderDynamic,
} from '@coursera-web/enterprise-admin-xapi';
import { SkillSetBankPageDynamic } from '@coursera-web/enterprise-skillset-bank';
import { McpIntegrationDynamic, WorkdayConfigurationDynamic } from '@coursera-web/external-integrations-configuration';
import { LtiToolRegistrationDynamic } from '@coursera-web/lti-tool-registration';

import { Route as CourseraRoute } from '@coursera/react-router';

import AdminDashboardApp from 'bundles/admin-dashboard/components/AdminDashboardApp';
import EnterpriseAdminPage from 'bundles/admin-dashboard/components/AdminDashboardPage';
import WithGlobalNavAndStyleGuide from 'bundles/admin-dashboard/components/WithGlobalNavAndStyleGuide';
import loadOnRoute from 'bundles/common/components/loadOnRoute';
import {
  COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH,
  FREEMIUM_INVITE_LEARNERS_PATH,
  FREEMIUM_PROGRAM_SETUP_PATH,
  FREEMIUM_PROGRAM_SURVEY_PATH,
} from 'bundles/enterprise-admin-coursera-for-campus-basic/constants';
import FirstTimeRedirect from 'bundles/enterprise-admin-home/components/FirstTimeRedirect';
import { PageNotFound as NotFound } from 'bundles/enterprise-ui/components/NotFound';

const AdminLandingPageV2 = loadOnRoute(() => import('bundles/enterprise-admin-home/components/AdminLandingPageV2'));
/* Remove this once the e2e tests are migrated to the new home */
const AdminLandingPage = loadOnRoute(() => import('bundles/enterprise-admin-home/components/AdminLandingPage'));

const ProgramSummaryListPage = loadOnRoute(
  () => import('bundles/enterprise-admin-home/components/ProgramSummaryListPage')
);

// program

const ProgramDetailsApp = loadOnRoute(
  () => import('bundles/enterprise-admin-program-home/components/ProgramDetailsApp')
);
const ProgramDetailsSettingsContainer = loadOnRoute(ProgramDetailsSettingsContainerDynamic);
const ManageLearnersContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-program-users/components/ManageLearnersContainer')
);
const ProgramDetailsCurriculum = loadOnRoute(
  () => import('bundles/enterprise-admin-program-catalog/components/ProgramCatalogPageContainer')
);

const ProgramCatalogPage = loadOnRoute(
  () => import('bundles/enterprise-admin-program-catalog/components/catalog/ProgramCatalogPage')
);

const ProgramCollectionsPage = loadOnRoute(
  () => import('bundles/enterprise-admin-program-catalog/components/catalog/ProgramCollectionsPage')
);
const ProgramLearningPathsPage = loadOnRoute(
  () => import('bundles/enterprise-admin-program-catalog/components/catalog/ProgramLearningPathsPage')
);
const ProgramAssignedContentPage = loadOnRoute(
  () => import('bundles/enterprise-admin-program-catalog/components/catalog/ProgramAssignedContentPage')
);
const ProgramCareerAcademyPage = loadOnRoute(
  () => import('bundles/enterprise-admin-program-catalog/components/catalog/ProgramCareerAcademyPage')
);

const ProgramDetailsMain = loadOnRoute(
  () => import('bundles/enterprise-admin-program-home/components/ProgramDetailsMain')
);
const ProgramDetailsLeaderboard = loadOnRoute(
  () => import('bundles/enterprise-admin-program-leaderboard/components/ProgramDetailsLeaderboard')
);
const ProgramSkillsPage = loadOnRoute(
  () => import('bundles/enterprise-admin-program-catalog/components/skills/ProgramSkillsPage')
);
const SkillTrackingHome = loadOnRoute(() => import('bundles/skills-common/SkillTrackingHome'));
const ProgramDetailsSkillSets = loadOnRoute(
  () => import('bundles/enterprise-admin-program-home/components/ProgramDetailsSkillSets')
);

const ProgramDetailsLevelSetsTracking = loadOnRoute(() =>
  import('bundles/enterprise-admin-program-levelsets/components/ProgramDetailsLevelSetsTracking').then(
    (module) => module.ProgramDetailsLevelSetsTracking
  )
);

const ProgramDetailsGradebook = loadOnRoute(
  () => import('bundles/enterprise-admin-program-gradebook/components/ProgramDetailsGradebook')
);

// program creation
const InviteOptions = loadOnRoute(() => import('bundles/enterprise-admin-program-creation/components/InviteOptions'));
const ContentConfirmation = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/ContentConfirmation')
);
const ProgramCreationPage = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/ProgramCreationPage')
);
const ProgramShell = loadOnRoute(() => import('bundles/enterprise-admin-program-creation/components/ProgramShell'));
const ProgramShellConfirmation = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/ProgramShellConfirmation')
);
const InviteConfirmation = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/InviteConfirmation')
);
const CurationOptions = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/CurationOptions')
);

const RolesSelection = loadOnRoute(() => import('bundles/enterprise-career-academy/components/RoleSelectionContainer'));

const LearnerAccessType = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/LearnerAccessType')
);
const TemplateOrCustomCollection = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/TemplateOrCustomCollection')
);
const CollectionShell = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/CollectionShell')
);
const SkillSetCurationOptions = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/SkillSetsCurationOptions')
);
const SkillSetSelection = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/SkillSetSelection')
);
const ReconfigureSkillSets = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/ReconfigureSkillSets')
);
const RemoveSkillSets = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/RemoveSkillSets')
);
const ReviewSkillSetSelection = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/ReviewSkillSetSelection')
);
const CollectionContentSearch = loadOnRoute(
  () => import('bundles/enterprise-admin-product-search/components/CollectionContentSearch')
);
const ChooseProgram = loadOnRoute(() => import('bundles/enterprise-admin-program-creation/components/ChooseProgram'));
const ChooseContract = loadOnRoute(() => import('bundles/enterprise-admin-program-creation/components/ChooseContract'));
const IndividualInvites = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/IndividualInvites')
);
const RegisteredDomains = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/RegisteredDomains')
);
const ProgramShellContract = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/ProgramShellContract')
);
const ExampleProgram = loadOnRoute(() => import('bundles/enterprise-admin-program-creation/components/ExampleProgram'));

const AcademicDisciplineSelection = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/AcademicDisciplinesSelection')
);
const ReviewCareers = loadOnRoute(() => import('bundles/enterprise-admin-program-creation/components/ReviewCareers'));
const BadgeConfiguration = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/BadgeConfiguration')
);
const NewLearningPath = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/NewLearningPath')
);
const LearningPathObjectives = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/LearningPathObjectives')
);
const ChooseContentContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/ChooseContentContainer')
);
const LearningPathContentSearch = loadOnRoute(
  () => import('bundles/enterprise-admin-program-creation/components/LearningPathContentSearch')
);

// learning path library
const LearningPathLibraryContext = loadOnRoute(LearningPathLibraryContextDynamic);
const LearningPathLibrary = loadOnRoute(LearningPathLibraryDynamic);
const LearningPathLibraryBadgePage = loadOnRoute(LearningPathLibraryBadgePageDynamic);

// collections

const CollectionPage = loadOnRoute(() => import('bundles/enterprise-admin-collection/components/CollectionPage'));
const CollectionContentPage = loadOnRoute(
  () => import('bundles/enterprise-admin-collection/components/CollectionContentPage')
);

// collection discovery
const CollectionDiscoveryContext = loadOnRoute(CollectionDiscoveryContextDynamic);
const CollectionSelectionConfirmationPage = loadOnRoute(CollectionSelectionConfirmationPageDynamic);
const PresetCollectionPage = loadOnRoute(PresetCollectionPageDynamic);
const CollectionCategoryPage = loadOnRoute(CollectionCategoryPageDynamic);
const TaggedCollectionsPage = loadOnRoute(TaggedCollectionsPageDynamic);
const CollectionDiscoveryPage = loadOnRoute(CollectionDiscoveryPageDynamic);
const CollectionLibraryPage = loadOnRoute(CollectionLibraryPageDynamic);

// account
const AdminDashboardAccount = loadOnRoute(
  () => import('bundles/enterprise-admin-account/components/AdminDashboardAccount')
);
const InstallmentReceipt = loadOnRoute(() => import('bundles/enterprise-admin-account/components/InstallmentReceipt'));

const ManagePaymentMethodRouter = loadOnRoute(
  () => import('bundles/enterprise-admin-account/components/ManagePaymentMethodRouter')
);
const AddSeatsContainer = loadOnRoute(() => import('bundles/enterprise-admin-account/components/AddSeatsContainer'));
const ManageLicenses = loadOnRoute(() => import('bundles/enterprise-admin-account/components/ManageLicenses'));
const CancelPlanContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-account/components/CancelPlanContainer')
);

// skills
const TSPContainer = loadOnRoute(() => import('bundles/enterprise-admin-skills/components/TSPContainer'));
const TSPReport = loadOnRoute(() => import('bundles/enterprise-admin-skills/components/TSPReport'));
const TSPFAQ = loadOnRoute(() => import('bundles/enterprise-admin-skills/components/TSPFAQ'));
const TSPSkillSetCourseRecommendations = loadOnRoute(
  () => import('bundles/enterprise-admin-skills/components/TSPSkillSetCourseRecommendations')
);
const SkillSetBankPage = loadOnRoute(SkillSetBankPageDynamic);

const EditSkillsetPage = loadOnRoute(() => import('bundles/enterprise-skillset-form/components/EditSkillsetPage'));

const OrganizationSkillSetsContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-skills/components/OrganizationSkillSetsContainer')
);

const NewSkillsetPage = loadOnRoute(() => import('bundles/enterprise-skillset-form/components/NewSkillsetPage'));
// users

const AdminDashboardUsersApp = loadOnRoute(
  () => import('bundles/enterprise-admin-users/components/AdminDashboardUsersApp')
);

const AddContentToCollectionPage = loadOnRoute(
  () => import('bundles/enterprise-admin-collection/components/AddContentToCollectionPage')
);

const LearnersPage = loadOnRoute(LearnersPageDynamic);
const ProgramLearnersPage = loadOnRoute(ProgramLearnersPageDynamic);
const AdminDashboardLearnersContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-users/components/AdminDashboardLearnersContainer')
);
const AdminsPage = loadOnRoute(() => import('bundles/enterprise-admin-management/components/AdminsPage'));
const ManageAdminsPage = loadOnRoute(() => import('bundles/enterprise-admin-management/components/ManageAdminsPage'));
const AddAdminFlow = loadOnRoute(() => import('bundles/enterprise-admin-management/components/AddAdminFlow'));
const NewAdminPage = loadOnRoute(() => import('bundles/enterprise-admin-management/components/add-admin/NewAdminPage'));
const AdminProgramsPage = loadOnRoute(
  () => import('bundles/enterprise-admin-management/components/add-admin/AdminProgramsPage')
);
const ReviewAdminPage = loadOnRoute(
  () => import('bundles/enterprise-admin-management/components/add-admin/ReviewAdminPage')
);
const AdminInvitationsPage = loadOnRoute(
  () => import('bundles/enterprise-admin-management/components/add-admin/AdminInvitationsPage')
);

const AdminPermissionsPage = loadOnRoute(
  () => import('bundles/enterprise-admin-management/components/PermissionsPage')
);

const UserFilterBuilder = loadOnRoute(
  () => import('bundles/enterprise-admin-user-filters/components/UserFilterBuilder')
);
const LearnerPage = loadOnRoute(LearnerPageDynamic);

// messages

const AdminDashboardMessagesApp = loadOnRoute(
  () => import('bundles/enterprise-admin-messages/components/AdminDashboardMessagesApp')
);
const AdminDashboardMessagesPage = loadOnRoute(
  () => import('bundles/enterprise-admin-messages/components/AdminDashboardMessagesPage')
);
const AutomatedMessages = loadOnRoute(() => import('bundles/enterprise-admin-messages/components/AutomatedMessages'));
const SentMessages = loadOnRoute(() => import('bundles/enterprise-admin-messages/components/SentMessages'));
const CreateMessage = loadOnRoute(() => import('bundles/enterprise-admin-messages/components/CreateMessage'));

// contracts

const AdminContractsContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-contracts/components/AdminContractsContainer')
);
const CreateContractContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-contracts/components/CreateContractContainer')
);
const MigrationTools = loadOnRoute(() => import('bundles/enterprise-admin-contracts/components/MigrationTools'));
const RenewContract = loadOnRoute(() => import('bundles/enterprise-admin-contracts/components/RenewContract'));
const UpsellContractContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-contracts/components/UpsellContractContainer')
);
const ContractDetailsContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-contracts/components/ContractDetailsContainer')
);

// other

const AdminDashboardSettings = loadOnRoute(AdminDashboardSettingsDynamic);

const SsoSettingsContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-sso-settings/components/SsoSettingsContainer')
);

// Analytics

const AnalyticsPage = loadOnRoute(() => import('bundles/enterprise-admin-analytics/components/AnalyticsPage'));

const AnalyticsDashboardsPage = loadOnRoute(
  () => import('bundles/enterprise-admin-analytics/components/AnalyticsDashboardsPage')
);

const AnalyticsReports = loadOnRoute(
  () => import('bundles/enterprise-admin-analytics/components/AnalyticsReportsPageV2')
);

const ConfigureXapiSettingsPage = loadOnRoute(ConfigureXapiSettingsPageDynamic);

const EditXapiSettingsPage = loadOnRoute(EditXapiSettingsPageDynamic);

const IntegrationSettings = loadOnRoute(IntegrateSettingsDynamic);
const LtiToolRegistration = loadOnRoute(LtiToolRegistrationDynamic);
const WorkdayConfiguration = loadOnRoute(WorkdayConfigurationDynamic);
const McpIntegration = loadOnRoute(McpIntegrationDynamic);
const IntegrationSettingsWrapper = loadOnRoute(IntegrationSettingsWrapperDynamic);

const AcademicIntegrity = loadOnRoute(
  () => import('bundles/academic-integrity/components/course-profiles/CourseProfile')
);
const SCIMSetupWrapper = loadOnRoute(SCIMSetupWrapperDynamic);
const SCIMConfigPage = loadOnRoute(SCIMConfigPageDynamic);
const SCIMGroupMappingPage = loadOnRoute(SCIMGroupMappingDynamic);

const CustomizeOrganizationAdminSettings = loadOnRoute(CustomizeOrganizationAdminSettingsDynamic);

const SSOLockedOutSetting = loadOnRoute(
  () => import('bundles/enterprise-admin-sso-settings/components/SSOLockedOutSetting')
);

const XapiContextProvider = loadOnRoute(XapiContextProviderDynamic);

const UnsubscribePage = loadOnRoute(UnsubscribePageDynamic);

const CourseBuilderContainer = loadOnRoute(
  () => import('bundles/enterprise-admin-custom-courses/components/CustomCoursesContainer')
);

const CustomCoursesApp = loadOnRoute(
  () => import('bundles/enterprise-admin-custom-courses/components/CustomCoursesApp')
);

const ContentAssignmentProgramPage = loadOnRoute(
  () => import('bundles/enterprise-admin-assigned-content/components/ContentAssignmentProgramPage')
);

const MyOrgs = loadOnRoute(() => import('bundles/admin-dashboard/components/MyOrgs'));

const ContentAssignmentPage = loadOnRoute(
  () => import('bundles/enterprise-admin-assigned-content/components/ContentAssignmentPage')
);
const ContentLibraryPage = loadOnRoute(
  () => import('bundles/enterprise-admin-assigned-content/components/ContentLibraryPage')
);

const AssignmentInsightsPage = loadOnRoute(
  () => import('bundles/enterprise-admin-assigned-content/components/AssignmentInsightsPage')
);

const DownlaodPage = loadOnRoute(() => import('bundles/admin-dashboard/components/DownloadPage'));

// program builder
const AiProgramBuilderContextProvider = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/AiProgramBuilderContext')
);
const AiProgramBuilderNewProgramPage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/AiProgramBuilderNewProgramPage')
);
const AiProgramBuilderExistingProgramPage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/AiProgramBuilderExistingProgramPage')
);
const AiProgramBuilderFileUploadPage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/AiProgramBuilderFileUploadPage')
);
const AiProgramBuilderRecommendationTypePage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/AiProgramBuilderRecommendationTypePage')
);
const AiProgramBuilderContractPage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/AiProgramBuilderContractPage')
);
const AiProgramBuilderFileProcessingPage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/AiProgramBuilderFileProcessingPage')
);
const AiProgramBuilderPage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/AiProgramBuilderPage')
);
const AiProgramBuilderBadgePage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/AiProgramBuilderBadgePage')
);
const AiProgramBuilderFailurePage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/AiProgramBuilderFailurePage')
);

const ProgramBuilderEntryPoint = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/ProgramBuilderEntryPoint')
);

const ProgramBuilderContractPage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/ProgramBuilderContractPage')
);

const ProgramBuilderContextProvider = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/ProgramBuilderContext')
);
const ProgramBuilderOutlinePage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/ProgramBuilderOutlinePage')
);
const ProgramBuilderContentPage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/ProgramBuilderContentPage')
);
const ProgramBuilderBadgePage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/ProgramBuilderBadgePage')
);

const CurationBuilderContextProvider = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/CurationBuilderContext')
);
const CurationBuilderPage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/CurationBuilderPage')
);
const CurationBuilderContentPage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/CurationBuilderContentPage')
);
const CurationBuilderBadgePage = loadOnRoute(
  () => import('bundles/enterprise-admin-ai-program-builder/components/CurationBuilderBadgePage')
);

const scrollToTopOnNavigate: ChangeHook = (prevState, nextState) => {
  if (nextState.location.action !== 'PUSH') {
    // history navigation
    return;
  }

  if (nextState.location.pathname !== prevState.location.pathname) {
    // always on path navigation
    window.scrollTo(0, 0);
  }
};

export default (
  <Route path="o" component={AdminDashboardApp} onChange={scrollToTopOnNavigate}>
    <Redirect from="collections" to="/collections-tool/admin" />
    <Redirect from="browseCollections" to="/collections-tool/browse" />
    <Route path="my" getComponent={MyOrgs} />
    <Route path="download" getComponent={DownlaodPage} />
    <CourseraRoute
      path=":thirdPartySlug"
      component={EnterpriseAdminPage}
      handle={{
        tracking: {
          data: (params) => {
            return {
              enterprise: {
                organizationSlug: params.thirdPartySlug,
                ...(params?.programSlug ? { programSlug: params.programSlug } : {}),
              },
              page: {
                slug: params.dashboardName || params.tagId || params.categoryId || params.presetCollectionId,
              },
            };
          },
        },
      }}
    >
      <Route path="xapi" component={XApiFormsWrapper}>
        <Route path="create" getComponent={ConfigureXapiSettingsPage} />
        <Route path="edit" getComponent={EditXapiSettingsPage} />
      </Route>
      <Route path="admin" component={WithGlobalNavAndStyleGuide}>
        <Redirect
          from="insights/:programSlug/:contentType/:contentId"
          to="programs/:programSlug/curriculum"
          query={{ tab: 'ASSIGNED_CONTENT' }}
        />
        <Route
          path="assignment/:programSlug/:productType/:productSlug/insights"
          getComponent={AssignmentInsightsPage}
        />
        <Route path="settings" getComponent={XapiContextProvider}>
          <Route getComponent={AdminDashboardSettings}>
            <Route path="integrate" getComponent={IntegrationSettingsWrapper}>
              <IndexRoute getComponent={IntegrationSettings} />
              <Route path="lti-tool-registration" getComponent={LtiToolRegistration} />
              <Route path="workday-configuration" getComponent={WorkdayConfiguration} />
              <Route path="mcp" getComponent={McpIntegration} />
            </Route>
            <Route path="academic-integrity" getComponent={AcademicIntegrity} />
            <Route path="scim" getComponent={SCIMSetupWrapper}>
              <IndexRoute getComponent={SCIMConfigPage} />
              <Route path="group-mapping" getComponent={SCIMGroupMappingPage} />
            </Route>
            <Route path="sso" getComponent={SSOLockedOutSetting} />
            <IndexRoute getComponent={CustomizeOrganizationAdminSettings} />
          </Route>
        </Route>
        <Route path="ssoconfig" getComponent={SsoSettingsContainer} />
        <Redirect from="permissions" to="users/permissions" />
        <CourseraRoute
          path="analytics"
          getComponent={AnalyticsPage}
          handle={{ tracking: { data: () => ({ page: { type: 'enterprise_admin_analytics' } }) } }}
        >
          <CourseraRoute path="reports" handle={{ tracking: { data: () => ({ page: { tab: 'reports' } }) } }}>
            <IndexRoute getComponent={AnalyticsReports} />
            <Route path=":reportId" getComponent={AnalyticsReports} />
            <Route path=":reportType/:actionType" getComponent={AnalyticsReports} />
          </CourseraRoute>
          <Redirect from="micro-badge" to="learning-paths" />
          <CourseraRoute
            path=":dashboardName"
            getComponent={AnalyticsDashboardsPage}
            handle={{ tracking: { data: (params) => ({ page: { tab: params.dashboardName } }) } }}
          />
          <IndexRedirect to="learner-activity" />
        </CourseraRoute>
        <CourseraRoute
          path="custom-courses"
          getComponent={CustomCoursesApp}
          handle={{ tracking: { data: () => ({ page: { type: 'enterprise_admin_custom_courses' } }) } }}
        >
          <IndexRoute getComponent={CourseBuilderContainer} />
        </CourseraRoute>
        <CourseraRoute
          path="skills-tracking"
          getComponent={SkillTrackingHome}
          handle={{
            tracking: { data: () => ({ page: { type: 'enterprise_admin_skillsets', tab: 'skills_tracking' } }) },
          }}
        />
        <CourseraRoute
          path="skill-sets"
          handle={{
            tracking: { data: () => ({ page: { type: 'enterprise_admin_skillsets', tab: 'skills_sets' } }) },
          }}
        >
          <IndexRoute getComponent={OrganizationSkillSetsContainer} />
          <Route path=":tspSlug" getComponent={TSPContainer}>
            <Route path="report" getComponent={TSPReport} />
            <Route path="recommendations" getComponent={TSPSkillSetCourseRecommendations} />
            <Route path="faq" getComponent={TSPFAQ} />
          </Route>
        </CourseraRoute>
        <CourseraRoute
          path="programs"
          handle={{ tracking: { data: () => ({ page: { type: 'enterprise_admin_programs' } }) } }}
        >
          <IndexRoute getComponent={ProgramSummaryListPage} />
          <Route path={FREEMIUM_INVITE_LEARNERS_PATH} component={() => null} />
          <Route path={FREEMIUM_PROGRAM_SETUP_PATH} component={() => null} />
          <CourseraRoute
            path="contracts"
            handle={{ tracking: { data: () => ({ page: { type: 'enterprise_admin_contracts' } }) } }}
          >
            <IndexRoute getComponent={AdminContractsContainer} />
            <Route path="migration-tools" getComponent={MigrationTools} />
            <Route path="renew/:contractId" getComponent={RenewContract} />
            <Route path="upsell/:contractId" getComponent={UpsellContractContainer} />
            <Route path="details/:contractId" getComponent={ContractDetailsContainer} />
          </CourseraRoute>
          <Route path=":programSlug" getComponent={ProgramDetailsApp}>
            <IndexRedirect to="main" />
            <Route path="manage-learners" getComponent={ManageLearnersContainer} />
            <CourseraRoute
              path="learners"
              getComponent={ProgramLearnersPage}
              handle={{ tracking: { data: () => ({ page: { tab: 'enterprise_admin_program_learners' } }) } }}
            />
            <Route path="curriculum" getComponent={ProgramDetailsCurriculum} />
            <CourseraRoute
              path="catalog"
              getComponent={ProgramCatalogPage}
              handle={{ tracking: { data: () => ({ page: { tab: 'enterprise_admin_program_catalog' } }) } }}
            >
              <Route path="assigned-content" getComponent={ProgramAssignedContentPage} />
              <Route path="career-academy" getComponent={ProgramCareerAcademyPage} />
              <Route path="skills" getComponent={ProgramSkillsPage} />
              <Route path="collections" getComponent={ProgramCollectionsPage} />
              <Route path="learning-paths" getComponent={ProgramLearningPathsPage} />
              <IndexRedirect to="assigned-content" />
            </CourseraRoute>
            <CourseraRoute
              path="settings"
              handle={{ tracking: { data: () => ({ page: { tab: 'enterprise_admin_program_settings' } }) } }}
            >
              <IndexRoute getComponent={ProgramDetailsSettingsContainer} />
              <Route path="academic-integrity" getComponent={AcademicIntegrity} />
              <Route path="general" getComponent={ProgramDetailsSettingsContainer} />
            </CourseraRoute>
            <CourseraRoute
              path="leaderboard"
              getComponent={ProgramDetailsLeaderboard}
              handle={{ tracking: { data: () => ({ page: { tab: 'enterprise_admin_program_leaderboard' } }) } }}
            />
            <CourseraRoute
              path="skill-sets"
              getComponent={ProgramDetailsSkillSets}
              handle={{ tracking: { data: () => ({ page: { tab: 'enterprise_admin_program_skillsets' } }) } }}
            />
            <CourseraRoute
              path="levelsets-tracking"
              getComponent={ProgramDetailsLevelSetsTracking}
              handle={{ tracking: { data: () => ({ page: { tab: 'enterprise_admin_program_levelsets_tracking' } }) } }}
            />
            <CourseraRoute
              path="gradebook"
              getComponent={ProgramDetailsGradebook}
              handle={{ tracking: { data: () => ({ page: { tab: 'enterprise_admin_program_gradebook' } }) } }}
            />
            <Route path={COURSERA_FOR_CAMPUS_BASIC_SETUP_PATH} component={() => null} />
            <Route path={FREEMIUM_PROGRAM_SURVEY_PATH} component={() => null} />
            <CourseraRoute
              path="main"
              getComponent={ProgramDetailsMain}
              handle={{ tracking: { data: () => ({ page: { tab: 'enterprise_admin_program_home' } }) } }}
            />
          </Route>
        </CourseraRoute>
        <Route path="account">
          <Route path="receipt/:contractId/:transactionId" getComponent={InstallmentReceipt} />
          <Route path="addseats/:contractId" getComponent={AddSeatsContainer} />
          <Route path="autorenew/:enterpriseContractId" getComponent={CancelPlanContainer} />
          <Route path="cancel/:enterpriseContractId" getComponent={CancelPlanContainer} />
          <Route path="payment-method/add" getComponent={ManagePaymentMethodRouter} />
          <Route path="payment-method/:paymentWalletId" getComponent={ManagePaymentMethodRouter} />
          <Route path="manage-licenses/:contractId" getComponent={ManageLicenses} />
          <IndexRoute getComponent={AdminDashboardAccount} />
        </Route>
        <CourseraRoute
          path="messages"
          getComponent={AdminDashboardMessagesApp}
          handle={{ tracking: { data: () => ({ page: { type: 'enterprise_admin_messages' } }) } }}
        >
          <Route path="list" getComponent={AdminDashboardMessagesPage}>
            <Route path="automated" getComponent={AutomatedMessages} />
            <Route path="sent" getComponent={SentMessages} />
            <IndexRedirect to="automated" />
          </Route>
          <Route path="new" getComponent={CreateMessage} />
          <IndexRedirect to="list/automated" />
        </CourseraRoute>
        <CourseraRoute
          path="users"
          getComponent={AdminDashboardUsersApp}
          handle={{ tracking: { data: () => ({ page: { type: 'enterprise_admin_users' } }) } }}
        >
          <Redirect from="permissions" to="admins" />
          <Route path="admins" getComponent={AdminsPage} />
          <IndexRoute getComponent={AdminDashboardLearnersContainer} />
        </CourseraRoute>
        <CourseraRoute
          path="learners"
          getComponent={LearnersPage}
          handle={{ tracking: { data: () => ({ page: { type: 'enterprise_admin_learners' } }) } }}
        />
        <Route path="management" getComponent={ManageAdminsPage} />
        <Route path="content/:programSlug">
          <Route path="collection/:collectionId" getComponent={CollectionPage} />
          <Route path="learning-path/:badgeTemplateId" getComponent={CollectionPage} />
        </Route>
        <Route path="learner/:learnerId" getComponent={LearnerPage} />
        <CourseraRoute
          path="home"
          getComponent={AdminLandingPageV2}
          handle={{ tracking: { data: () => ({ page: { type: 'enterprise_admin_home_v2' } }) } }}
        />
        <Route path="old-home" getComponent={AdminLandingPage} />
        <IndexRedirect to="home" />
        <Route path="*" component={NotFound} />
      </Route>
      <Route path="program-creation" getComponent={ProgramCreationPage}>
        <IndexRoute getComponent={ProgramShell} />
        <Route path="example-program" getComponent={ExampleProgram} />
        <Route path="choose-program" getComponent={ChooseProgram} />
        <Route path="contract" getComponent={ProgramShellContract} />
        <Route path=":programSlug">
          <Route path="shell-confirmation" getComponent={ProgramShellConfirmation} />
          <Route path="learner-access-type" getComponent={LearnerAccessType} />
          <Route path="curation-options" getComponent={CurationOptions} />
          <Route path="career-academy-roles-selection" getComponent={RolesSelection} />
          <Route path="skillset-curation-options" getComponent={SkillSetCurationOptions} />
          <Route path="skillset-selection" getComponent={SkillSetSelection} />
          <Route path="reconfigure-skillsets" getComponent={ReconfigureSkillSets} />
          <Route path="remove-skillsets" getComponent={RemoveSkillSets} />
          <Route path="review-skillsets" getComponent={ReviewSkillSetSelection} />
          <Route path="template-or-custom-collection" getComponent={TemplateOrCustomCollection} />
          <Route path="new-collection" getComponent={CollectionShell} />
          <Route path="collection-content-search" getComponent={CollectionContentSearch} />
          <Route path="content-confirmation" getComponent={ContentConfirmation} />
          <Route path="choose-contract" getComponent={ChooseContract} />
          <Route path="invite" getComponent={InviteOptions} />
          <Route path="individual-invites" getComponent={IndividualInvites} />
          <Route path="registered-domains" getComponent={RegisteredDomains} />
          <Route path="invite-confirmation" getComponent={InviteConfirmation} />
          <Route path="academic-disciplines-selection" getComponent={AcademicDisciplineSelection} />
          <Route path="review-careers" getComponent={ReviewCareers} />
          <Route path="badge-configuration" getComponent={BadgeConfiguration} />
          <Route path="new-learning-path" getComponent={NewLearningPath} />
          <Route path="learning-path-objectives" getComponent={LearningPathObjectives} />
          <Route path="choose-content-container" getComponent={ChooseContentContainer} />
          <Route path="learning-path-content-search" getComponent={LearningPathContentSearch} />
        </Route>
      </Route>
      <Route path="user-filter-builder" getComponent={UserFilterBuilder} />
      <Route path="skillset/:skillsetSlug" getComponent={EditSkillsetPage} />
      <Route path="skillset-bank" getComponent={SkillSetBankPage} />
      <Route path="new-skillset" getComponent={NewSkillsetPage} />
      <Route path="new-admin" getComponent={AddAdminFlow}>
        <IndexRoute getComponent={NewAdminPage} />
        <Route path="permissions" getComponent={AdminPermissionsPage} />
        <Route path="programs" getComponent={AdminProgramsPage} />
        <Route path="review" getComponent={ReviewAdminPage} />
        <Route path="invitations" getComponent={AdminInvitationsPage} />
      </Route>
      <Route path="new-contract" getComponent={CreateContractContainer} />
      <Route path="assign" getComponent={ContentAssignmentProgramPage} />
      <Route path="assign/:productType/:productSlug" getComponent={ContentAssignmentProgramPage} />
      <Route path="content-assignment/:programSlug/:productType/:productSlug" getComponent={ContentAssignmentPage} />
      <Route path="content-library/:programSlug" getComponent={ContentLibraryPage} />
      <Route path="content/:programSlug">
        <Route path="learning-path/:badgeTemplateId" getComponent={CollectionContentPage} />
        <Route path="collection/:collectionId" getComponent={CollectionContentPage} />
      </Route>
      <Route path="add-content" getComponent={AddContentToCollectionPage} />
      <Route path="add-content/:productType/:productSlug" getComponent={AddContentToCollectionPage} />
      <Route path="collection-discovery/:programSlug" getComponent={CollectionDiscoveryContext}>
        <Route path="confirmation" getComponent={CollectionSelectionConfirmationPage} />
        <Route path="collections/:categoryId/:tagId" getComponent={TaggedCollectionsPage} />
        <Route path="by/:categoryId" getComponent={CollectionCategoryPage} />
        <Route path=":presetCollectionId" getComponent={PresetCollectionPage} />
        <IndexRoute getComponent={CollectionDiscoveryPage} />
      </Route>
      <Route path="collection-library/:programSlug" getComponent={CollectionDiscoveryContext}>
        <IndexRoute getComponent={CollectionLibraryPage} />
      </Route>
      <Route path="learning-path-library/:programSlug" getComponent={LearningPathLibraryContext}>
        <IndexRoute getComponent={LearningPathLibrary} />
        <Route path="generate-badges" getComponent={LearningPathLibraryBadgePage} />
      </Route>
      <CourseraRoute
        path="program-builder"
        getComponent={AiProgramBuilderContextProvider}
        handle={{ tracking: { data: () => ({ page: { type: 'enterprise_admin_learning_program' } }) } }}
      >
        <Route path="new-program" getComponent={AiProgramBuilderNewProgramPage} />
        <Route
          path="existing-program/:programDraftSlug/:recommendationType"
          getComponent={AiProgramBuilderExistingProgramPage}
        />
        <Route path="file-upload(/:programDraftSlug)(/:flowVariant)" getComponent={AiProgramBuilderFileUploadPage} />
        <Route path="recommendation-type" getComponent={AiProgramBuilderRecommendationTypePage} />
        <Route path="contract" getComponent={AiProgramBuilderContractPage} />
        <Route
          path="file-processing(/:programDraftSlug)(/:flowVariant)"
          getComponent={AiProgramBuilderFileProcessingPage}
        />
        <IndexRoute getComponent={AiProgramBuilderPage} />
        <Route path="failure" getComponent={AiProgramBuilderFailurePage} />
        <Route path=":programDraftSlug(/:flowVariant)">
          <IndexRoute getComponent={AiProgramBuilderPage} />
          <Route path="badge" getComponent={AiProgramBuilderBadgePage} />
        </Route>
      </CourseraRoute>
      <Route path="new-program" getComponent={ProgramBuilderContextProvider}>
        <IndexRoute getComponent={ProgramBuilderEntryPoint} />
        <Route path="contract" getComponent={ProgramBuilderContractPage} />
        <Route path="outline(/:programOutlineId)" getComponent={ProgramBuilderOutlinePage} />
        <Route path="content/:programOutlineId" getComponent={ProgramBuilderContentPage} />
        <Route path="badge/:programDraftSlug" getComponent={ProgramBuilderBadgePage} />
      </Route>
      <Route path="new-curation/:parentProgramSlug/:curationType" getComponent={CurationBuilderContextProvider}>
        <IndexRoute getComponent={CurationBuilderPage} />
        <Route path="content" getComponent={CurationBuilderContentPage} />
        <Route path="badge" getComponent={CurationBuilderBadgePage} />
      </Route>
      <Route path="first-time-redirect" component={FirstTimeRedirect} />
      <Route path="unsubscribe" getComponent={UnsubscribePage} />
      <IndexRoute component={NotFound} />
    </CourseraRoute>
    <IndexRedirect to="/internal/enterprise/organizations" />
    <Route path="*" component={NotFound} />
  </Route>
);
